import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { Select, Button, Divider, Space } from "antd";
import {
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    HighlightOutlined,
    UndoOutlined,
    RedoOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    BarsOutlined,
    UnorderedListOutlined,
    OrderedListOutlined,
    LinkOutlined,
} from "@ant-design/icons";
import { useEffect, forwardRef, useImperativeHandle } from "react";
import styles from "../styles/RichTextEditor.module.css";

const TEXT_STYLE_OPTIONS = [
    { value: "paragraph", label: "ข้อความปกติ" },
    { value: "heading1", label: "หัวข้อใหญ่" },
    { value: "heading2", label: "หัวข้อกลาง" },
    { value: "heading3", label: "หัวข้อเล็ก" },
];

const RichTextEditor = forwardRef(({ value = "", onChange = () => { } }, ref) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Highlight,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Link.configure({ openOnClick: false }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    useImperativeHandle(ref, () => ({
        insertVariable: (code) => {
            if (!editor) return;
            editor.chain().focus().insertContent(`{{${code}}}`).run();
        },
        focus: () => editor?.chain().focus().run(),
    }), [editor]);

    if (!editor) return null;

    const currentTextStyle = () => {
        if (editor.isActive("heading", { level: 1 })) return "heading1";
        if (editor.isActive("heading", { level: 2 })) return "heading2";
        if (editor.isActive("heading", { level: 3 })) return "heading3";
        return "paragraph";
    };

    const onTextStyleChange = (val) => {
        if (val === "paragraph") {
            editor.chain().focus().setParagraph().run();
        } else {
            const level = Number(val.replace("heading", ""));
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    const onLink = () => {
        const url = window.prompt("ใส่ลิงก์ URL");
        if (url) editor.chain().focus().setLink({ href: url }).run();
    };

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <Space size={4}>
                    <Button
                        size="small"
                        type="text"
                        icon={<UndoOutlined />}
                        disabled={!editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}
                    />
                    <Button
                        size="small"
                        type="text"
                        icon={<RedoOutlined />}
                        disabled={!editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}
                    />
                </Space>
                <Divider type="vertical" />
                <Select
                    size="small"
                    value={currentTextStyle()}
                    onChange={onTextStyleChange}
                    options={TEXT_STYLE_OPTIONS}
                    style={{ width: 130 }}
                />
                <Divider type="vertical" />
                <Space size={4}>
                    <Button
                        size="small"
                        type={editor.isActive("bold") ? "primary" : "text"}
                        icon={<BoldOutlined />}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive("italic") ? "primary" : "text"}
                        icon={<ItalicOutlined />}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive("strike") ? "primary" : "text"}
                        icon={<StrikethroughOutlined />}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive("underline") ? "primary" : "text"}
                        icon={<UnderlineOutlined />}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive("highlight") ? "primary" : "text"}
                        icon={<HighlightOutlined />}
                        onClick={() => editor.chain().focus().toggleHighlight().run()}
                    />
                </Space>
                <Divider type="vertical" />
                <Space size={4}>
                    <Button
                        size="small"
                        type={editor.isActive({ textAlign: "left" }) ? "primary" : "text"}
                        icon={<AlignLeftOutlined />}
                        onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive({ textAlign: "center" }) ? "primary" : "text"}
                        icon={<AlignCenterOutlined />}
                        onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive({ textAlign: "right" }) ? "primary" : "text"}
                        icon={<AlignRightOutlined />}
                        onClick={() => editor.chain().focus().setTextAlign("right").run()}
                    />
                    {/* <Button
                        size="small"
                        type={editor.isActive({ textAlign: "justify" }) ? "primary" : "text"}
                        icon={<BarsOutlined />}
                        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                    /> */}
                </Space>
                <Divider type="vertical" />
                <Space size={4}>
                    <Button
                        size="small"
                        type={editor.isActive("bulletList") ? "primary" : "text"}
                        icon={<UnorderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    />
                    <Button
                        size="small"
                        type={editor.isActive("orderedList") ? "primary" : "text"}
                        icon={<OrderedListOutlined />}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    />
                    <Button size="small" type="text" icon={<LinkOutlined />} onClick={onLink} />
                </Space>
            </div>
            <EditorContent editor={editor} className={styles.content} />
        </div>
    );
});

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;